import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Table, Tag, Space, message, Popconfirm, Input, Row, Col } from 'antd';
import { adminUpdateOrderItemStatusAsync, adminBulkUpdateOrderItemStatusAsync, adminIncomeAsync } from '../adminSlice';
import { useTableParams } from '../../../hooks/useTableParams';

const IncomeTable = () => {
  const dispatch = useDispatch();
  const { incomeData, isLoading } = useSelector(state => state.admin);
  const user = useSelector(state => state.user.data);
  const incomeTotal = useSelector(state => state.admin.incomeData.total) || 0;
  const [dataSource, setDataSource] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const { tableParams, localSearch, handleTableChange, handleSearchChange, fetchData } = useTableParams(adminIncomeAsync);

  useEffect(() => {
    if (incomeData?.orders && incomeData.orders.length > 0) {
      const rows = [];
      incomeData.orders.forEach(order => {
        order.orderItems.forEach(item => {
          rows.push({
            key: `${order._id}-${item._id}`,
            isGuestData: order.isGuestData,
            products: {
              productName: item.plantName,
              description: `Order ID: ${order._id}`,
              imgLink: item.images?.url || "https://upload.wikimedia.org/wikipedia/commons/c/ce/Emojione_1F331.svg",
              link: `/product/${item.plant}`,
            },
            sale: item.quantity,
            amount: `₹${item.price * item.quantity}`,
            tag: order.orderStatus?.status || 'pending',
            status: order.orderStatus?.message || 'Processing',
            action: order.orderStatus?.status || 'pending',
          });
        });
      });
      setDataSource(rows);
    } else {
      setDataSource([]);
    }
  }, [incomeData]);

  const handleUpdateStatus = async (key, status, statusMessage) => {
    try {
      const [orderId, itemId] = key.split('-');
      // adminUpdateOrderItemStatusAsync expects 'id' to be the vendorOrderId
      const res = await dispatch(adminUpdateOrderItemStatusAsync({ id: orderId, orderId, itemId, status, message: statusMessage })).unwrap();
      if (res.status) {
        message.success(res.message);
        fetchData();
      }
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  const handleBulkUpdateStatus = async (status, statusMessage) => {
    try {
      const res = await dispatch(adminBulkUpdateOrderItemStatusAsync({ keys: selectedRowKeys, status, message: statusMessage })).unwrap();
      if (res.status) {
        message.success(res.message);
        setSelectedRowKeys([]);
        fetchData();
      }
    } catch (error) {
      message.error("Failed to perform bulk update");
    }
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns = [
    {
      title: 'Product Name',
      dataIndex: 'products',
      key: 'products',
      width: "25%",
      render: ({ productName, description, imgLink, link }) => {
        return (
          <a href={link} className='d-flex text-decoration-none hover-product-name'>
            <div style={{ width: "50px", height: "50px" }} className='border p-1 rounded me-1'>
              <img style={{ width: "100%", height: "100%", objectFit: "cover" }} src={imgLink} alt="plants flowers" />
            </div>
            <div className="d-flex flex-column ms-1 justify-content-start mt-1">
              <h6 className='h6 fw-bold text-black'>{productName}</h6>
              <p className='text-secondary fw-lighter' style={{ fontSize: "12px" }}>{description}</p>
            </div>
          </a>
        );
      },
    },
    {
      title: 'Quantity',
      dataIndex: 'sale',
      key: 'sale',
      sorter: true,
      render: (sale) => <span className='text-secondary'>{sale}</span>
    },
    {
      title: 'Total Revenue',
      dataIndex: 'amount',
      key: 'amount',
      sorter: true,
      render: (amount) => <span className='text-secondary'>{amount}</span>
    },
    {
      title: 'Tag',
      dataIndex: 'tag',
      key: 'tag',
      sorter: true,
      filters: [
        { text: 'Pending', value: 'pending' },
        { text: 'Placed', value: 'placed' },
        { text: 'Delivered', value: 'delivered' },
        { text: 'Completed', value: 'completed' },
        { text: 'Rejected', value: 'rejected' },
      ],
      render: (tag) => {
        const color = tag.toLowerCase() === 'pending' ? 'orange' : tag.toLowerCase() === 'delivered' ? 'green' : tag.toLowerCase() === 'placed' ? 'blue' : 'red';
        return <Tag color={color}>{tag.toUpperCase()}</Tag>
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status) => <span className='text-secondary'>{status}</span>
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        const action = record.action || 'pending';
        const isGuestAndRealData = (user?.email === "guest-admin@plantseller.com" || user?.email === "guest-seller@plantseller.com") && !record.isGuestData;
        return (
          <Space size={'small'}>
            {action.toLowerCase() === 'pending' && (
              <>
                <Popconfirm title="Accept this order?" disabled={isGuestAndRealData} onConfirm={() => handleUpdateStatus(record.key, 'placed', 'Order Accepted')}>
                  <button className='btn btn-sm btn-success py-1 px-2' disabled={isGuestAndRealData} style={{ fontSize: "12px" }}>Accept</button>
                </Popconfirm>
                <Popconfirm title="Reject this order?" disabled={isGuestAndRealData} onConfirm={() => handleUpdateStatus(record.key, 'rejected', 'Order Rejected')}>
                  <button className='btn btn-sm btn-danger py-1 px-2 text-white' disabled={isGuestAndRealData} style={{ fontSize: "12px" }}>Reject</button>
                </Popconfirm>
              </>
            )}
            {action.toLowerCase() === 'placed' && (
              <Popconfirm title="Mark this order as Delivered?" disabled={isGuestAndRealData} onConfirm={() => handleUpdateStatus(record.key, 'delivered', 'Order Delivered')}>
                <button className='btn btn-sm btn-info py-1 px-2 text-white' disabled={isGuestAndRealData} style={{ fontSize: "12px" }}>Deliver</button>
              </Popconfirm>
            )}
          </Space>
        )
      }
    },
  ];

  const hasSelected = selectedRowKeys.length > 0;
  const isGuestAdmin = user?.email === "guest-admin@plantseller.com" || user?.email === "guest-seller@plantseller.com";
  const disableBulkActions = isGuestAdmin && selectedRowKeys.some(key => {
    const row = dataSource.find(r => r.key === key);
    return row && !row.isGuestData;
  });

  return (
    <div className="w-100">
      <Row justify="space-between" align="middle" gutter={[16, 16]} className="mb-4">
        <Col xs={24} md={8}>
          {/* Title can go here if needed */}
        </Col>
        <Col xs={24} md={16} style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search by Order ID..."
            allowClear
            prefix={<span role="img" aria-label="search">🔍</span>}
            value={localSearch}
            onChange={handleSearchChange}
            style={{ width: '100%', maxWidth: '300px' }}
          />
        </Col>
      </Row>

      {hasSelected && (
        <div className="d-flex align-items-center mb-3 p-3 bg-light border rounded gap-2">
          <span className="fw-bold me-2">{selectedRowKeys.length} items selected:</span>
          <Popconfirm title={`Approve payment for ${selectedRowKeys.length} selected items?`} disabled={disableBulkActions} onConfirm={() => handleBulkUpdateStatus('placed', 'Payment Approved')}>
            <button className="btn btn-sm btn-success py-1 px-2 text-white" disabled={disableBulkActions} style={{ fontSize: "12px" }}>Bulk Approve</button>
          </Popconfirm>
          <Popconfirm title={`Reject payment for ${selectedRowKeys.length} selected items?`} disabled={disableBulkActions} onConfirm={() => handleBulkUpdateStatus('rejected', 'Payment Rejected')}>
            <button className="btn btn-sm btn-danger py-1 px-2 text-white" disabled={disableBulkActions} style={{ fontSize: "12px" }}>Bulk Reject</button>
          </Popconfirm>
          <Popconfirm title={`Mark payment for ${selectedRowKeys.length} selected items as Delivered?`} disabled={disableBulkActions} onConfirm={() => handleBulkUpdateStatus('delivered', 'Payment Delivered')}>
            <button className="btn btn-sm btn-info py-1 px-2 text-white" disabled={disableBulkActions} style={{ fontSize: "12px" }}>Bulk Deliver</button>
          </Popconfirm>
        </div>
      )}
      <Table
        rowSelection={rowSelection}
        loading={isLoading}
        dataSource={dataSource}
        columns={columns}
        pagination={{
          ...tableParams.pagination,
          total: incomeTotal,
          showSizeChanger: true,
          position: ['bottomCenter']
        }}
        onChange={handleTableChange}
        className='overflow-x-auto'
      />
    </div>
  )
}

export default IncomeTable
